import {
  Avatar,
  Box,
  Button,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect } from 'react'
import { IconButton } from 'utoniq-ui'

import {
  Maybe,
  PaymentDetailFragment,
  PaymentMethod,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { Loading } from '@/components/organisms'
import {
  ExpiredCardModal,
  PaymentErrorModal,
  Require3DSecureModal,
} from '@/components/organisms/functionals/PaymentErrorModal'
import { LegalInformationLinksCell } from '@/components/organisms/legalInformation'
import { MembershipJoinedModal } from '@/components/organisms/membership/membershipJoinedModal'
import { ArtistThemeProvider } from '@/components/styles'
import {
  StorageImage,
  termsOfServiceUrl,
  useMessage,
  useRouter,
  useTranslation,
} from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { generateCreditCardLogoPath } from '@/core/creditCard'
import { MembershipSubscriptionProduct } from '@/core/membership'

export * from './fragments'

export type State = {
  loading: boolean
  isSubmitting: boolean
  artistId: string
  product?: MembershipSubscriptionProduct
  paymentSource?: Maybe<PaymentDetailFragment>
  paymentMethod?: PaymentMethod
  favoriteMemberInfo?: {
    id: string
    name: string
    avatar: string
  }
  openExpiredCardModal: boolean
  openPaymentErrorModal: boolean
  openRequire3DSecureModal: boolean
  joined: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSubmit: () => Promise<void>
  onCloseRequire3DSecureModal: () => void
  onCloseExpiredCardModal: () => void
  onClosePaymentErrorModal: () => void
}

export const ArtistMembershipPurchaseConfirmationTemplate: Template<
  State,
  Action
> = ({
  state,
  onSubmit,
  onCloseRequire3DSecureModal,
  onCloseExpiredCardModal,
  onClosePaymentErrorModal,
}) => {
  const { t } = useTranslation('artistMembershipPurchaseConfirmation')
  const { router, generatePath, routerNames, routerPages } =
    useRouter<{ status: string }>()
  const { showMessage, showError } = useMessage()

  // 外部キャリア決済画面からのリダイレクトで戻ってきた際
  // トーストメッセージを表示する処理
  useEffect(() => {
    switch (router.query.status) {
      case 'cancel':
        showMessage(t('carrierCancel'))
        break
      case 'error':
        showError(t('carrierError'))
        break
      default:
        break
    }
  }, [router.query.status])

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ArtistMembershipPurchaseConfirmation} />
      {state.joined ? (
        <MembershipJoinedModal artistId={state.artistId} />
      ) : (
        <BaseLayout
          header={{
            title: t('title'),
            left: (
              <IconButton.Back
                size="medium"
                sx={{ ml: '-8px' }}
                onClick={() =>
                  router.replace(
                    generatePath(routerPages.ArtistMembershipPurchaseProceed, {
                      artistId: state.artistId,
                      productId: state.product?.productId ?? '#',
                    })
                  )
                }
              />
            ),
          }}
        >
          {state.loading ? (
            <LoadingCell />
          ) : (
            <>
              <Typography
                variant="h5"
                sx={{ bgcolor: 'grey.50', px: 5, py: 2, mt: 2 }}
                color="text.secondary"
              >
                {t('plan', 'プラン')}
              </Typography>
              <Stack gap={1} sx={{ p: 5 }}>
                <Typography variant="h5">{state.product?.title}</Typography>
                <Typography variant="subtitle1" color="primary.main">
                  {t(`${state.product?.plan?.interval}Course`)}
                </Typography>
                <Typography variant="subtitle1">
                  {t(`${state.product?.plan?.interval}CoursePrice`, {
                    amount: state.product?.plan?.formattedAmount,
                  })}
                </Typography>
              </Stack>
              {/* 推しメンバー情報の表示 */}
              {state.favoriteMemberInfo && (
                <>
                  <Typography
                    variant="h5"
                    sx={{ bgcolor: 'grey.50', px: 5, py: 2, mt: 2 }}
                    color="text.secondary"
                  >
                    {t('favoriteMember', '推しメンバー')}
                  </Typography>
                  <Stack spacing={2} sx={{ px: 5, pt: 4, pb: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar
                        src={state.favoriteMemberInfo.avatar}
                        alt="favorite member avatar"
                        sx={{
                          width: 64,
                          height: 64,
                        }}
                      />
                      <Typography variant="subtitle1">
                        {state.favoriteMemberInfo.name}
                      </Typography>
                    </Stack>
                  </Stack>
                </>
              )}
              <Typography
                variant="h5"
                sx={{ bgcolor: 'grey.50', px: 5, py: 2, mt: 2 }}
                color="text.secondary"
              >
                {t('paymentInformation')}
              </Typography>
              {/* クレジットカード決済の場合 */}
              {state.paymentMethod === PaymentMethod.CreditCard && (
                <Stack spacing={1} sx={{ p: 5 }}>
                  <Stack direction="row" alignItems="center">
                    <Typography variant="h5">{t('creditCard')}</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center">
                    <StorageImage
                      height={25}
                      src={generateCreditCardLogoPath(
                        state.paymentSource?.brand
                      )}
                      target="common"
                    />
                    <Typography variant="body2">
                      {t('last4Digits', {
                        number: state.paymentSource?.last4,
                      })}
                    </Typography>
                  </Stack>
                  <Typography variant="caption">
                    {t(`${state.product?.plan.interval}CreditCardNote`)}
                  </Typography>
                </Stack>
              )}
              {/* キャリア決済の場合 */}
              {state.paymentMethod === PaymentMethod.Au && (
                <Stack spacing={1} sx={{ p: 5 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h5">{t('au')}</Typography>
                  </Stack>
                  <Typography variant="caption">{t('carrierDesc')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('carrierDesc2')}
                  </Typography>
                </Stack>
              )}
              {state.paymentMethod === PaymentMethod.Softbank && (
                <Stack spacing={1} sx={{ p: 5 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h5">{t('softbank')}</Typography>
                  </Stack>
                  <Typography variant="caption">{t('carrierDesc')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('carrierDesc2')}
                  </Typography>
                </Stack>
              )}
              {state.paymentMethod === PaymentMethod.Docomo && (
                <Stack spacing={1} sx={{ p: 5 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h5">{t('docomo')}</Typography>
                  </Stack>
                  <Typography variant="caption">{t('carrierDesc')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('carrierDesc2')}
                  </Typography>
                </Stack>
              )}
              {/* コンビニ払いの場合 */}
              {state.paymentMethod === PaymentMethod.Konbini && (
                <Stack spacing={1} sx={{ p: 5 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h5">
                      {t('convenienceStore')}
                    </Typography>
                  </Stack>
                  <Typography variant="caption">
                    {t('convenienceStoreDesc')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('convenienceStoreDesc2')}
                  </Typography>
                </Stack>
              )}
              <Typography
                variant="h5"
                sx={{ bgcolor: 'grey.50', px: 5, py: 2, mt: 2 }}
                color="text.secondary"
              >
                {t('totalPaymentAmount')}
              </Typography>
              <Stack sx={{ p: 5 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ py: 2 }}
                >
                  <Typography variant="subtitle1" color="text.secondary">
                    {t(`${state.product?.plan?.interval}Fee`)}
                  </Typography>
                  <Typography variant="body2">
                    ￥{state.product?.plan?.formattedAmount}
                  </Typography>
                </Stack>
                <Divider />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ py: 2 }}
                >
                  <Typography variant="subtitle1" color="text.secondary">
                    {t('total')}
                  </Typography>
                  <Typography variant="h6">
                    ￥{state.product?.plan?.formattedAmount}
                  </Typography>
                </Stack>
                <Stack gap={2}>
                  {state.paymentMethod === PaymentMethod.CreditCard && (
                    <>
                      <Stack direction="row">
                        <Typography variant="caption">・</Typography>
                        <Typography variant="caption">
                          {t(`${state.product?.plan?.interval}NoteCreditCard1`)}
                        </Typography>
                      </Stack>
                      <Stack direction="row">
                        <Typography variant="caption">・</Typography>
                        <Typography variant="caption">
                          {t('noteCreditCard2')}
                        </Typography>
                      </Stack>
                    </>
                  )}
                  <Typography variant="caption">{t('agreeTerm')}</Typography>
                </Stack>
              </Stack>

              <LinkTextCell />
              <LegalInformationLinksCell sx={{ px: 5 }} />
              <Stack alignItems="center" sx={{ pb: 8 }}>
                <Button
                  size="large"
                  disabled={state.isSubmitting}
                  loading={state.isSubmitting}
                  sx={{ width: 212 }}
                  onClick={async () => {
                    await onSubmit()
                  }}
                >
                  {t('join', '加入する')}
                </Button>
              </Stack>
            </>
          )}
          <Require3DSecureModal
            open={state.openRequire3DSecureModal}
            onClose={onCloseRequire3DSecureModal}
          />

          <ExpiredCardModal
            open={state.openExpiredCardModal}
            onClose={onCloseExpiredCardModal}
          />

          <PaymentErrorModal
            open={state.openPaymentErrorModal}
            onClose={onClosePaymentErrorModal}
          />
        </BaseLayout>
      )}
    </ArtistThemeProvider>
  )
}

type LinkTextCellProps = {}
export const LinkTextCell: Cell<LinkTextCellProps> = () => {
  const { t } = useTranslation('artistMembership')
  return (
    <CellFrame
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{ px: 5, py: 6 }}
    >
      <Link
        component="a"
        variant="caption"
        underline="none"
        target="_blank"
        href={termsOfServiceUrl()}
      >
        {t('termsOfService')}
      </Link>
      <Link
        component="a"
        variant="caption"
        target="_blank"
        href="https://about.utoniq.com/privacy-policy"
        underline="none"
        sx={{ pt: 2 }}
      >
        {t('privacyPolicy')}
      </Link>
    </CellFrame>
  )
}

const LoadingCell: Cell = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '90dvh',
      }}
    >
      <Loading />
    </Box>
  )
}
